import { useState } from 'react';
import { Input } from '@paulownia/shared/components/ui/input';
import { Label } from '@paulownia/shared/components/ui/label';
import { Button } from '@paulownia/shared/components/ui/button';
import { X, Plus } from 'lucide-react';
import type { PortionSize } from '@paulownia/shared/types';

interface WeightPackagedFieldsProps {
  price?: number;
  portionSizes?: PortionSize[];
  onPriceChange: (price: number | undefined) => void;
  onPortionSizesChange: (sizes: PortionSize[]) => void;
}

export function WeightPackagedFields({
  price,
  portionSizes = [],
  onPriceChange,
  onPortionSizesChange,
}: WeightPackagedFieldsProps) {
  const [priceKgInput, setPriceKgInput] = useState(price ? String(price) : '');
  // Local state for portion price inputs, keyed by portion id
  const [priceInputs, setPriceInputs] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    portionSizes.forEach(p => {
      initial[p.id] = p.price !== undefined ? String(p.price) : '';
    });
    return initial;
  });

  const getPriceInput = (id: string) => priceInputs[id] || '';

  const setPriceInput = (id: string, value: string) => {
    setPriceInputs(prev => ({ ...prev, [id]: value }));
  };

  const addPortionSize = () => {
    const newId = `portion_${Date.now()}`;
    const newSize: PortionSize = {
      id: newId,
      label: '',
      price: 0,
    };
    setPriceInputs(prev => ({ ...prev, [newId]: '' }));
    onPortionSizesChange([...portionSizes, newSize]);
  };

  const updatePortionSize = (index: number, updates: Partial<PortionSize>) => {
    const newSizes = portionSizes.map((size, i) =>
      i === index ? { ...size, ...updates } : size
    );
    onPortionSizesChange(newSizes);
  };

  const removePortionSize = (index: number) => {
    onPortionSizesChange(portionSizes.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-4">
      {/* Price per kg - indicative */}
      <div className="space-y-2">
        <Label htmlFor="price-kg">Prix au kilo indicatif (€/kg)</Label>
        <Input
          id="price-kg"
          type="text"
          inputMode="decimal"
          value={priceKgInput}
          onChange={(e) => {
            const val = e.target.value;
            if (val === '' || /^\d*[.,]?\d{0,2}$/.test(val)) {
              setPriceKgInput(val);
              const normalized = val.replace(',', '.');
              const parsed = parseFloat(normalized);
              onPriceChange(val === '' ? undefined : (isNaN(parsed) ? undefined : parsed));
            }
          }}
          placeholder="ex: 26.00"
        />
        <p className="text-xs text-gray-500">
          Affiché au client comme référence. N'affecte pas le prix des formats.
        </p>
      </div>

      {/* Portion sizes */}
      <div className="space-y-3">
        <Label>Formats disponibles *</Label>

        {portionSizes.length > 0 ? (
          <div className="space-y-3">
            {portionSizes.map((portion, index) => (
              <div key={index} className="p-3 bg-gray-50 rounded-lg border space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-gray-500">
                    Format {index + 1}
                  </span>
                  <button
                    type="button"
                    onClick={() => removePortionSize(index)}
                    className="p-1 text-gray-400 hover:text-red-600"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                <div className="flex gap-3">
                  {/* Label */}
                  <div className="flex-1 space-y-1">
                    <Label className="text-xs">Nom</Label>
                    <Input
                      value={portion.label}
                      onChange={(e) => updatePortionSize(index, { label: e.target.value })}
                      placeholder="ex: 250g, 500g, 1kg..."
                      className="h-9"
                    />
                  </div>

                  {/* Price */}
                  <div className="w-28 space-y-1">
                    <Label className="text-xs">Prix (€)</Label>
                    <Input
                      type="text"
                      inputMode="decimal"
                      value={getPriceInput(portion.id)}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === '' || /^\d*[.,]?\d{0,2}$/.test(val)) {
                          setPriceInput(portion.id, val);
                          const normalized = val.replace(',', '.');
                          const parsed = parseFloat(normalized);
                          updatePortionSize(index, {
                            price: val === '' ? 0 : (isNaN(parsed) ? 0 : parsed),
                          });
                        }
                      }}
                      placeholder="0.00"
                      className="h-9"
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-gray-500 italic py-2">
            Aucun format défini. Ajoutez au moins un format.
          </p>
        )}

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={addPortionSize}
          className="w-full"
        >
          <Plus className="h-4 w-4 mr-1" />
          Ajouter un format
        </Button>
      </div>
    </div>
  );
}
